import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAccessToken } from "../../services/authApi";
import { fieldReader } from "../../services/loanApi";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import Loading from "../layout/Loading";
import '../lighteningorder/placeorder.css';
import { Table, Select, Input, Button, Pagination, Modal, message, Tooltip } from "antd";
import { DownloadOutlined, FormOutlined } from "@ant-design/icons";
import {
    getExistingDocumentId,
    createDocument,
    getAttachmentUploadUrl,
    uploadAttachmentBlob,
    assignAttachmentToDocument,
    generateFilledPDF
} from '../../services/documentApi';

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const PLACEORDER_DOMAIN = process.env.REACT_APP_LIGHTNING_DOC_URL;

const LIGHTNING_TEST_FLAG = String(process.env.REACT_APP_LIGHTNING_TEST || "").toLowerCase() === "true" ? 1 : 0;

const LighteningDocsOrder = () => {
    const [loanNumber, setLoanNumber] = useState('');
    const [loanID, setLoanID] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userName, setUserName] = useState('');
    //const [isTest, setIsTest] = useState(1);
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState("");
    const [dataLoaded, setDataLoaded] = useState(false);
    const [authInitialized, setAuthInitialized] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
    const [showPopup, setShowPopup] = useState(false);
    const [loanTypeValue, setLoanTypeValue] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [knacklyData, setKnacklyData] = useState(null);
    const [docLoading, setDocLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [token, setToken] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);


    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, [loanTypeValue]);

    useEffect(() => {
        // Stop loader immediately for restricted users
        if (loanTypeValue && !isBusinessLoan(loanTypeValue)) {
            setIsLoading(false);
            return;
        }

        if (authInitialized && loanNumber && isBusinessLoan(loanTypeValue)) {
            fetchLogs();
        }
    }, [authInitialized, loanNumber, loanTypeValue]);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async (loanObject) => {
                loanObject.all().then(async (loan) => {
                    setLoanID(loan.loan.id);
                    setLoanNumber(loan.loan.loanNumber);
                    await getToken(false);
                    info("Lightening doc order page loaded", { loanid: loan.loan.id || "N/A" });
                    const tpoAuthObject = await window.elli.script.getObject("auth");
                    const user = await tpoAuthObject.getUser();
                    if (user) {
                        const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`;
                        setUserEmail(user?.email ?? 'testuseremail');
                        setUserName(fullName ?? 'testuser');
                    }
                    setAuthInitialized(true);
                    const authCode = await tpoAuthObject.createAuthCode();
                    const token = await getAccessToken(authCode);
                    const fieldsToFetch = ["CX.LOAN.TYPE"];
                    const fieldResponse = await fieldReader(token, fieldsToFetch, loan.loan.id);
                    setLoanTypeValue(fieldResponse["CX.LOAN.TYPE"]);
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
            setAuthInitialized(true);
        } finally {

        }
    };
    const isBusinessLoan = (loanTypeValue) =>
        loanTypeValue?.toLowerCase().includes("business");

    const fetchLogs = async () => {
        if (!loanNumber) return;

        setIsLoading(true);
        setError("");
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const user = await tpoAuthObject.getUser();

            if (user) {
                const authCode = await tpoAuthObject.createAuthCode();
                const url = `${API_DOMAIN}/api/lightning-doc/logs?authCode=${encodeURIComponent(authCode)}&loanNumber=${encodeURIComponent(loanNumber)}`;

                try {
                    const response = await fetch(url, {
                        method: "GET",
                        headers: { "Content-Type": "application/json" },
                    });

                    if (!response.ok) throw new Error("Failed to fetch messages");
                    const result = await response.json();
                    //sort logs by createdDateTime desc
                    const sortedLogs = result.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));
                    setLogs(sortedLogs);
                    setDataLoaded(true);
                } catch (err) {
                    console.error(err);
                    setError("Failed to fetch logs");
                    setDataLoaded(true);
                } finally {
                    setIsLoading(false);
                }
            }
        } catch (error) {
            const errorCode = logError("LIGHTNING_DOC_ERROR");
            logAppInsightError(`${errorCode} - fetching lightning `, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "LightningForm",
            });
            setError("Authentication error");
            setDataLoaded(true);
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        //setDataLoaded(false);
        setIsProcessing(true);
        setStatusMessage({ type: "", text: "" });

        try {
            const payload = {
                loannumber: loanNumber,
                loanid: loanID,
                test: LIGHTNING_TEST_FLAG,
                user: userName,
                email: userEmail,
                record_id: ""
            };

            const url = PLACEORDER_DOMAIN;
            const response = await axios.post(url, payload, {
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": process.env.REACT_APP_LIGHTNING_DOC_API_KEY || "",
                },
            });
            const { status, message: apiMessage } = response.data;

            if (status === "Success") {
                setStatusMessage({ type: "success", text: apiMessage });
                fetchLogs();
            } else if (status === "Error") {
                setStatusMessage({ type: "error", text: apiMessage });
            } else {
                setStatusMessage({ type: "warning", text: "Unexpected response from server" });
            }

        } catch (err) {
            setStatusMessage({ type: "error", text: `Failed to place order: ${err.message}` });
        } finally {
            setIsProcessing(false);
        }
    };

    // const toggleTestMode = () => {
    //     setIsTest(prev => prev === 1 ? 0 : 1);
    // };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "completed":
                return "bg-green-100 text-green-800";
            case "incomplete":
                return "bg-yellow-100 text-yellow-800";
            case "update":
                return "bg-pink-200 text-pink-800"
            default:
                return "bg-orange-100 text-orange-800";
        }
    };

    const DocumentIcon = () => (
        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    );
    const RefreshIcon = () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
    );

    const fetchKnacklyStatus = async (recordId) => {
        try {
            const response = await fetch(`${API_DOMAIN}/api/knackly/status/${recordId}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();

            if (result.status && result.status.toLowerCase() === "completed") {
                setKnacklyData(result);
                setIsModalVisible(true);
            } else {
                setDocLoading(false);
            }
        } catch (err) {
            console.error("Knackly API error:", err);
            setErrorMessage(`Failed to fetch document status: ${err.message}`);
        }
    };

    const refreshStatus = async (transactionId) => {
        try {
            const response = await fetch(`${API_DOMAIN}/api/knackly/update/${transactionId}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const result = await response.json();

            if (!response.ok) {
                const errorMsg = result?.error || `HTTP ${response.status}`;
                throw new Error(errorMsg);
            }

            setSuccessMessage(`Refreshed status: ${result.status}`);
            setTimeout(() => setSuccessMessage(""), 3000);

            fetchLogs();
        } catch (err) {
            console.error("Refresh failed:", err);
            setErrorMessage(`Failed to refresh status: ${err.message}`);
        }
    };



    const getToken = async (skipFieldLoad = false) => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
        } catch (err) {
            console.error("Token fetch failed", err);
        }
        finally {
        }
    };

    const MoveToEFolder = async (filesToUpload) => {

        try {
            for (const file of filesToUpload) {
                const proxyResponse = await fetch(`${API_DOMAIN}/api/knackly/proxy-knackly`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        publicUrl: file.publicUrl,
                        fileNameExt: file.name // or any filename you want
                    }),
                });


                if (!proxyResponse.ok) {
                    throw new Error(`Failed to fetch file: ${proxyResponse.statusText}`);
                }
                const pdfBlob = await proxyResponse.blob();
                const documentTitle = "LIGHTNING DOC ORDER";
                const fileName = file.name || "Lightning Doc Order.pdf";

                let documentId = await getExistingDocumentId(token, loanID, documentTitle);
                if (!documentId) {
                    documentId = await createDocument(token, loanID, documentTitle);
                }

                const uploadInfo = await getAttachmentUploadUrl(token, loanID, fileName, pdfBlob.size, pdfBlob.type);
                await uploadAttachmentBlob(uploadInfo.uploadUrl, uploadInfo.authorizationHeader, pdfBlob, pdfBlob.type);
                await assignAttachmentToDocument(token, loanID, documentId, uploadInfo.attachmentId);
            }
            setSuccessMessage(`Files moved to eFolder`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            const message = error?.message || "Failed to submit the form. Please try again.";
            console.error("PDF generation failed", error);
            logAppInsightError("Lightning order moving to eFolder failed Error", error);
            setErrorMessage(message);
            setTimeout(() => setSuccessMessage(""), 3000);
        }
        finally {
            // setDocLoading(false);
        }
    };

    const handleUpdateOrderClick = (logItem) => {
        if (!logItem?.transactionID) {
            setErrorMessage("Transaction ID is missing. Cannot update order.");
            return;
        }
        setSelectedLog(logItem);
        setShowUpdateModal(true);
    };

    const updateExistingOrder = async (logItem) => {
        if (!logItem?.transactionID) {
            setErrorMessage("Transaction ID is missing. Cannot update order.");
            return;
        }

        setIsProcessing(true);
        setStatusMessage({ type: "", text: "" });

        try {
            const payload = {
                loannumber: loanNumber,
                loanid: loanID,
                test: LIGHTNING_TEST_FLAG,
                user: userName,
                email: userEmail,
                record_id: logItem.transactionID, // <--- Pass transactionID as record_id
            };

            const url = PLACEORDER_DOMAIN;
            const response = await axios.post(url, payload, {
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": process.env.REACT_APP_LIGHTNING_DOC_API_KEY || "",
                },
            });

            const { status, message: apiMessage } = response.data;
            if (status === "Success") {
                setSuccessMessage(`Transaction updated successfully: ${apiMessage}`);
                setTimeout(() => setSuccessMessage(""), 3000);
                fetchLogs(); // Refresh logs
            } else if (status === "Error") {
                setErrorMessage(`Transaction updated with error: ${apiMessage}`);
                setTimeout(() => setSuccessMessage(""), 3000);
            } else {
                setErrorMessage("Unexpected response from server");
                setTimeout(() => setSuccessMessage(""), 3000);
            }
        } catch (err) {
            setErrorMessage(`Failed to update order: ${err.message}`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } finally {
            setIsProcessing(false);
        }
    };

    {
        isLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-t-4 border-gray-200 border-t-[#006aa9]"></div>
            </div>
        )
    }

    // Restricted access check
    if (loanTypeValue && !isBusinessLoan(loanTypeValue)) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
                <div className="bg-white shadow-md rounded-lg p-8 max-w-lg text-center border border-gray-200 relative -mt-20">
                    {/* Error Icon */}
                    <div className="flex justify-center mb-4">
                        <svg
                            className="w-12 h-12 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>

                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                        Restricted Access
                    </h2>
                    <p className="text-gray-600">
                        This page is available only for{" "}
                        <span className="font-medium text-[#006aa9]">
                            Business Purpose Loans
                        </span>
                        . Please navigate back to continue with your loan processing.
                    </p>
                </div>
            </div>
        );
    }


    return (
        <>
            {successMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">✅</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Success</div>
                        <div className="text-sm leading-relaxed break-words">{successMessage}</div>
                    </div>
                    <button
                        onClick={() => setSuccessMessage('')}
                        className="ml-4 text-green-500 hover:text-green-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
            {errorMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-red-100 border-l-4 border-red-600 text-red-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">❌</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Error</div>
                        <div className="text-sm leading-relaxed break-words">{errorMessage}</div>
                    </div>
                    <button
                        onClick={() => setErrorMessage('')}
                        className="ml-4 text-red-500 hover:text-red-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
            <div className="p-4">
                <hr className="my-1 border-gray-400 border-t-3 -mt-1" />
                {/* Order Service Section with Refresh and Place Order buttons on the right */}
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-md font-semibold text-gray-800"></h3>
                            <div className="flex items-center space-x-2">
                                {logs && logs.length > 0 && (
                                    <button
                                        onClick={fetchLogs}
                                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                                        title="Refresh logs"
                                    >
                                        <RefreshIcon />
                                    </button>
                                )}
                                {logs.length > 0 ? (
                                    <Button
                                        type="primary"
                                        className="px-3 py-1 bg-[#006aa9] text-white text-sm font-medium rounded hover:bg-[#1394e5] transition-colors"
                                        onClick={() => handleUpdateOrderClick(logs[0])} // pick first record or adjust as needed
                                    >
                                        Redraw Closing Package
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            setShowPopup(true);
                                            setError("");
                                            setStatusMessage({ type: "", text: "" });
                                        }}
                                        className="px-3 py-1 bg-[#006aa9] text-white text-sm font-medium rounded hover:bg-[#1394e5] transition-colors"
                                    >
                                        Create Closing Package
                                    </Button>
                                )}

                            </div>
                        </div>
                        {/* Logs Section */}
                        <div className="mt-6">
                            <h1 className="text-lg font-semibold text-gray-800 mb-4"></h1>
                            {error ? (
                                <div className="text-center text-red-600">{error}</div>
                            ) : logs && logs?.length > 0 ? (
                                <div className="bg-white rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr>
                                                    <th className="p-3 text-lm uppercase table-head">User Email</th>
                                                    <th className="p-3 table-head text-left">User Name</th>
                                                    <th className="p-3 table-head">Order Status</th>
                                                    <th className="p-3 table-head">Order Type</th>
                                                    <th className="p-3 table-head">Transaction ID</th>
                                                    <th className="p-3 table-head">Ordered Date</th>
                                                    <th className="p-3 table-head">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {logs?.map((item, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="p-3 table-body">{item?.userEmail ? item?.userEmail : ""}</td>
                                                        <td className="p-3 table-body">{item?.userName ? item?.userName : ""}</td>
                                                        <td className="p-3">
                                                            <span className={`px-2 py-1 table-body rounded-full ${getStatusColor(item?.orderStatus)}`}>
                                                                {item?.orderStatus ? item?.orderStatus : ""}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">
                                                            <span className={`px-2 py-1 table-body rounded-full ${item?.logLevel.toLowerCase() == "import" ? "CREATE" : getStatusColor(item?.logLevel)}`}>
                                                                {item?.logLevel.toLowerCase() == "import" ? "CREATE" : item?.logLevel.toLowerCase() == "update" ? "REDRAW" : item?.logLevel}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 table-body">{item?.transactionID ? item?.transactionID : ""}</td>
                                                        <td className="p-3 table-body">
                                                            {new Date(item?.createdDateTime).toLocaleString()}
                                                        </td>
                                                        <td className="text-center w-[80px]">
                                                            <div className="flex justify-center items-center gap-2">
                                                                {index === 0 ? (
                                                                    <>
                                                                        {/* <Tooltip title="Documents">
                                                                            <button
                                                                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                                onClick={() => fetchKnacklyStatus(item?.transactionID)}
                                                                            >
                                                                                <DocumentIcon />
                                                                            </button>
                                                                        </Tooltip> */}

                                                                        <Tooltip title="Refresh Order">
                                                                            <button
                                                                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                                onClick={() => {
                                                                                    if (!item?.transactionID) {
                                                                                        setErrorMessage("Transaction ID is missing. Cannot refresh status.");
                                                                                        return;
                                                                                    }
                                                                                    refreshStatus(item.transactionID);
                                                                                }}>
                                                                                <RefreshIcon />
                                                                            </button>
                                                                        </Tooltip>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {/* keeps spacing consistent even without Document icon */}
                                                                        {/* <div className="w-[30px]" /> */}
                                                                        <Tooltip title="Refresh Order">
                                                                            <button
                                                                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                                onClick={() => {
                                                                                    if (!item?.transactionID) {
                                                                                        setErrorMessage("Transaction ID is missing. Cannot refresh status.");
                                                                                        return;
                                                                                    }
                                                                                    refreshStatus(item.transactionID);
                                                                                }}
                                                                            >
                                                                                <RefreshIcon />
                                                                            </button>
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                                {/* New Update Order button */}
                                                                {/* <Tooltip title="Update Order">
                                                            <button
                                                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                onClick={() => handleUpdateOrderClick(item)}>
                                                                <FormOutlined className="text-gray-600 text-lg" />
                                                            </button>
                                                        </Tooltip> */}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                dataLoaded && <div className="text-center text-gray-600">No logs found.</div>
                            )}
                        </div>
                    </>
                )}
                {/* Popup Modal */}
                {showPopup && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">Lightning Docs Order</h2>
                                <button
                                    onClick={() => { setShowPopup(false); setError(""); setStatusMessage({ type: "", text: "" }); }}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Toggle Switch */}
                                {/* <div className="flex justify-between items-center border rounded-md px-3 py-2 bg-white">
                                    <span className="text-sm font-semibold uppercase">Create as Test File?</span>
                                    <button
                                        type="button"
                                        onClick={toggleTestMode}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isTest ? "bg-[#2080C3]" : "bg-gray-300"}`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isTest ? "translate-x-6" : "translate-x-1"}`}
                                        />
                                    </button>
                                </div> */}

                                {/* Import Button */}
                                <button
                                    type="submit"
                                    disabled={isProcessing}
                                    className="w-full btn-custom rounded 
                                    text-[#FFFFFF] bg-[#2080C3] 
                                    hover:text-[#E6EEF8] hover:bg-[#5DA5D9] 
                                    active:bg-white transition-colors duration-200
                                    flex items-center justify-center h-10"
                                >
                                    {isProcessing ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : "Place Lightning Doc Order"}
                                </button>

                                {/* Status Message */}
                                {statusMessage.text && (
                                    <div
                                        className={`mt-3 rounded-md p-3 text-sm font-medium ${statusMessage.type === "success"
                                            ? "bg-green-100 text-green-800"
                                            : statusMessage.type === "error"
                                                ? "bg-red-100 text-red-800"
                                                : "bg-yellow-100 text-yellow-800"
                                            }`}
                                    >
                                        {statusMessage.text}
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                )}

                <Modal
                    title={knacklyData?.label || "Documents"}
                    open={isModalVisible}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setSelectedRowKeys([]);
                        setSelectedRows([]);
                    }}
                    footer={null}
                    width={800}
                >
                    {successMessage && (
                        <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                            <div className="text-2xl">✅</div>
                            <div className="flex-1">
                                <div className="font-semibold mb-1">Success</div>
                                <div className="text-sm leading-relaxed break-words">{successMessage}</div>
                            </div>
                            <button
                                onClick={() => setSuccessMessage('')}
                                className="ml-4 text-green-500 hover:text-green-700 focus:outline-none transition-colors duration-200"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {docLoading ? (
                        <div className="text-center text-gray-600">Refreshing…</div>
                    ) : knacklyData?.status?.toLowerCase() === "completed" ? (
                        <>
                            {/* Upload button only when something is selected */}
                            {selectedRows.length > 0 && (
                                <div className="mb-3 flex justify-end">
                                    <Button
                                        type="primary"
                                        loading={isUploading}   // shows spinner
                                        disabled={isUploading}  // disables button
                                        onClick={async () => {
                                            setIsUploading(true);
                                            try {
                                                await MoveToEFolder(selectedRows);
                                            } catch (err) {
                                                setErrorMessage("Upload failed: " + err.message);
                                            } finally {
                                                setIsUploading(false); // re-enable button
                                            }
                                        }}
                                        className="px-3 py-1 text-white text-sm font-medium rounded transition-colors
                                                bg-[#006aa9] hover:bg-[#006aa9] focus:bg-[#006aa9] active:bg-[#006aa9]
                                                disabled:bg-[#006aa9] disabled:text-white disabled:opacity-100"
                                    >
                                        {isUploading ? "Uploading…" : "Upload to eFolder"}
                                    </Button>
                                </div>
                            )}

                            <Table
                                dataSource={knacklyData?.files || []}
                                rowKey="name"
                                pagination={false}
                                rowSelection={{
                                    selectedRowKeys,
                                    onChange: (keys, rows) => {
                                        setSelectedRowKeys(keys);
                                        setSelectedRows(rows);
                                    },
                                }}
                                columns={[
                                    {
                                        title: "File Name",
                                        dataIndex: "name",
                                        key: "name",
                                    }
                                    ,
                                    {
                                        title: "Download",
                                        key: "download",
                                        render: (_, record) => (
                                            <Button
                                                type="link"
                                                icon={<DownloadOutlined />}
                                                onClick={() => {
                                                    const link = document.createElement("a");
                                                    link.href = record.publicUrl;
                                                    link.download = record.fileName || "download";
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                    setSuccessMessage("Downloaded successfully");
                                                }}
                                            >
                                                Download
                                            </Button>
                                        ),
                                    },
                                ]}
                            />
                        </>
                    ) : (
                        <div className="text-center text-orange-600">Still in progress…</div>
                    )}
                </Modal>

                {/* confirmation modal popup for updating the trransaction */}
                <Modal
                    title="Update Lightning Doc Order"
                    open={showUpdateModal}
                    onCancel={() => {
                        if (!isProcessing) { // prevent cancel while processing
                            setShowUpdateModal(false);
                            setSelectedLog(null);
                        }
                    }}
                    footer={null}
                    maskClosable={false}
                    okButtonProps={{
                        className: "bg-[#006aa9] text-white rounded-md hover:bg-[#006aa9] focus:bg-[#006aa9] active:bg-[#006aa9]",
                    }}
                >

                    Are you sure you want to update this existing order?


                    <div className="flex justify-end space-x-2">
                        {!isProcessing && (
                            <Button onClick={() => setShowUpdateModal(false)}>No</Button>
                        )}
                        <Button
                            type="primary"
                            className="bg-[#006aa9] text-white rounded-md hover:bg-[#006aa9] focus:bg-[#006aa9] active:bg-[#006aa9]"
                            loading={isProcessing}
                            onClick={async () => {
                                setIsProcessing(true);
                                try {
                                    await updateExistingOrder(selectedLog);
                                    await fetchLogs(); // refresh logs after success
                                    setShowUpdateModal(false);
                                } catch (err) {
                                    setErrorMessage("Failed to update order: " + err.message);
                                } finally {
                                    setShowUpdateModal(false);
                                    setSelectedLog(null);
                                    setIsProcessing(false);
                                }
                            }}
                        >
                            {isProcessing ? "Processing..." : "Yes"}
                        </Button>
                    </div>
                </Modal >

            </div >
        </>
    );
};

export default LighteningDocsOrder;